import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, ZoomControl, useMap } from "react-leaflet";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const COLORS = {
  critical: "#dc2626",
  high: "#ea580c",
  medium: "#ca8a04",
  low: "#059669",
};

function FlyTo({ focus }) {
  const map = useMap();
  const lastKeyRef = useRef(null);
  useEffect(() => {
    if (!focus) return;
    const key = `${focus.lat}-${focus.lng}-${focus.key || ""}`;
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    map.flyTo([focus.lat, focus.lng], focus.zoom || 10, { duration: 1.2 });
  }, [focus, map]);
  return null;
}

function FocusPulse({ focus }) {
  if (!focus) return null;
  return (
    <CircleMarker
      center={[focus.lat, focus.lng]}
      radius={18}
      pathOptions={{ color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.15, weight: 2, dashArray: "4 4" }}
    >
      {focus.label && (
        <Tooltip direction="top" permanent offset={[0, -8]}>
          <div className="font-mono text-[11px]">
            <div className="font-semibold">{focus.label}</div>
            {focus.sub && <div className="text-slate-500">{focus.sub}</div>}
          </div>
        </Tooltip>
      )}
    </CircleMarker>
  );
}

export default function MapView({ markers, onMarkerClick, focus }) {
  return (
    <div className="relative h-full w-full" data-testid="map-view">
      <MapContainer
        center={[22.9734, 78.6569]}
        zoom={5}
        zoomControl={false}
        className="h-full w-full"
        style={{ background: "#eef2f6" }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap · CIMAP'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <ZoomControl position="bottomright" />
        <FlyTo focus={focus} />
        <FocusPulse focus={focus} />
        {markers.map((m, i) => {
          const color = COLORS[m.worst_severity] || "#475569";
          const radius = Math.min(22, 6 + Math.sqrt(m.count) * 1.6);
          return (
            <CircleMarker
              key={`${m.site}-${i}`}
              center={[m.lat, m.lng]}
              radius={radius}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.55, weight: 2 }}
              eventHandlers={{ click: () => onMarkerClick && onMarkerClick(m) }}
            >
              <Tooltip direction="top" offset={[0, -4]} opacity={1}>
                <div className="font-mono text-[11px]">
                  <div className="font-semibold">{m.site}</div>
                  <div className="text-slate-500">{m.state} · {m.count} signals</div>
                  <div>Risk {m.avg_risk} · {m.worst_severity}</div>
                </div>
              </Tooltip>
              <Popup>
                <div className="text-xs">
                  <div className="font-display font-bold text-sm mb-1">{m.site}</div>
                  <div className="text-slate-500 mb-2">{m.state}</div>
                  <div className="font-mono">Signals: <b>{m.count}</b></div>
                  <div className="font-mono">Avg risk: <b style={{ color }}>{m.avg_risk}</b></div>
                  <div className="font-mono">Sentiment: {m.avg_sentiment}</div>
                  <div className="mt-2 text-slate-500">{m.ventures.join(" · ")}</div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>

      <div className="absolute top-3 left-3 bg-white/95 border border-slate-200 rounded-sm p-3 text-[11px] font-mono shadow-sm z-[20]" data-testid="map-legend">
        <div className="kicker mb-2">Severity</div>
        <div className="space-y-1">
          {Object.entries(COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: v }} />
              <span className="capitalize">{k}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
